import FinancialSearchSelect from './FinancialSearchSelect';
// src/components/finance/InvoicesPanel.tsx

import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Send,
  Eye,
  Filter,
  User,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Settings,
  Download,
  Printer,
  Pencil,
  RotateCcw
} from 'lucide-react';
import { UnifiedPatient, Invoice, InvoiceChangeActor } from '../../types/unified-financial';
import { InvoiceService, InvoiceFilter, InvoiceSummary } from '../../services/InvoiceService';
import { InvoicePDFService } from '../../services/InvoicePDFService';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import InvoiceEditModal from './InvoiceEditModal';
import BankingConfigModal from './BankingConfigModal';
import type { BankingInfo } from '../../types/banking';

interface InvoicesPanelProps {
  patients: UnifiedPatient[];
  initialDueMonth?: string;
}

export default function InvoicesPanel({ patients, initialDueMonth }: InvoicesPanelProps) {
  const { user, selectedClient } = useAuth();
  const formatDate = (value?: string) => {
    if (!value) return '--';

    // Se for date-only (YYYY-MM-DD) — tipo DATE no Postgres — formatar sem aplicar fuso
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [yStr, mStr, dStr] = value.split('-');
      const y = Number(yStr);
      const m = Number(mStr);
      const d = Number(dStr);
      if (!y || !m || !d) return '--';
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }

    // Caso seja um timestamp ISO, parsear normalmente e mostrar no fuso local
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('pt-BR');
  };

  const formatMonthReference = (monthStr: string): string => {
    const [year, month] = monthStr.split('-');
    const monthNames = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} de ${year}`;
  };

  const [invoices, setInvoices] = useState<Array<Invoice & { patientName: string; patientId: string }>>([]);
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<(Invoice & { patientName: string; patientId: string }) | null>(null);
  const [filter, setFilter] = useState<InvoiceFilter>(() =>
    initialDueMonth ? { dueMonth: initialDueMonth } : {}
  );
  const [loading, setLoading] = useState(true);
  const [showBankingConfig, setShowBankingConfig] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<(Invoice & { patientName: string; patientId: string }) | null>(null);
  const [bankingInfo, setBankingInfo] = useState<BankingInfo>(() => InvoiceService.getBankingInfo());
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<(Invoice & { patientName: string; patientId: string }) | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const [revertReasonError, setRevertReasonError] = useState<string | null>(null);

  const invoiceActor: InvoiceChangeActor | null = useMemo(() => {
    if (!user?.userId || !selectedClient?.clientId || !selectedClient?.userRole) {
      return null;
    }
    return {
      userId: user.userId,
      userRole: selectedClient.userRole,
      clientId: selectedClient.clientId,
      displayName: user.userDisplayName,
      email: user.userEmail,
    };
  }, [user, selectedClient]);

  // Carrega faturas
  useEffect(() => {
    loadInvoices();
  }, [filter, selectedClient?.clientId]);

  useEffect(() => {
    if (!initialDueMonth) return;
    setFilter(prev =>
      prev.dueMonth === initialDueMonth
        ? prev
        : { ...prev, dueMonth: initialDueMonth }
    );
  }, [initialDueMonth]);

  useEffect(() => {
    void loadBankingInfo();
  }, [selectedClient?.clientId]);

  const loadInvoices = async () => {
    try {
      if (!selectedClient?.clientId) {
        setInvoices([]);
        setSummary(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const [invoicesData, summaryData] = await Promise.all([
        InvoiceService.getAllInvoices({ ...filter, clientId: selectedClient.clientId }),
        InvoiceService.getInvoicesSummary({ ...filter, clientId: selectedClient.clientId })
      ]);
      
      setInvoices(invoicesData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Erro ao carregar faturas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBankingInfo = async () => {
    if (!selectedClient?.clientId) {
      const defaultInfo = InvoiceService.getBankingInfo();
      setBankingInfo(defaultInfo);
      return defaultInfo;
    }

    const loadedBankingInfo = await InvoiceService.loadBankingInfo(selectedClient.clientId);
    setBankingInfo(loadedBankingInfo);
    return loadedBankingInfo;
  };

  const updateInvoiceStatus = async (patientId: string, invoiceId: string, newStatus: Invoice['status']) => {
    try {
      if (!selectedClient?.clientId) {
        alert('Tenant não selecionado');
        return;
      }

      if (!invoiceActor) {
        alert('Não foi possível identificar o usuário para auditoria.');
        return;
      }

      await InvoiceService.updateInvoiceStatus(
        patientId,
        invoiceId,
        newStatus,
        selectedClient.clientId,
        invoiceActor,
      );
      await loadInvoices(); // Recarregar
    } catch (error: any) {
      alert(`Erro ao atualizar status: ${error.message}`);
    }
  };

  const openRevertModal = (invoice: Invoice & { patientName: string; patientId: string }) => {
    if (!invoiceActor || invoiceActor.userRole !== 'admin') {
      toast.error('Somente administradores podem reverter pagamentos.');
      return;
    }
    if (invoice.status !== 'paid') {
      toast.error('Apenas faturas com status pago podem ser revertidas.');
      return;
    }
    setRevertTarget(invoice);
    setRevertReason('');
    setRevertReasonError(null);
    setShowRevertModal(true);
  };

  const closeRevertModal = () => {
    setShowRevertModal(false);
    setRevertTarget(null);
    setRevertReason('');
    setRevertReasonError(null);
  };

  const confirmRevert = async () => {
    if (!revertTarget) return;
    if (!selectedClient?.clientId) {
      setRevertReasonError('Tenant não selecionado');
      return;
    }
    if (!invoiceActor) {
      setRevertReasonError('Usuário não identificado para auditoria');
      return;
    }
    if (!revertReason || revertReason.trim().length < 10) {
      setRevertReasonError('Justificativa deve ter ao menos 10 caracteres');
      return;
    }

    try {
      await InvoiceService.revertInvoicePayment(
        revertTarget.patientId,
        revertTarget.invoiceId,
        selectedClient.clientId,
        invoiceActor,
        revertReason.trim(),
      );
      closeRevertModal();
      await loadInvoices();
      toast.success('Pagamento revertido com sucesso');
    } catch (err: any) {
      toast.error(`Erro ao reverter pagamento: ${err.message}`);
    }
  };

  const sendInvoiceWhatsApp = async (invoice: Invoice & { patientName: string; patientId: string }) => {
    const patient = patients.find(p => p.id === invoice.patientId);
    if (!patient) {
      alert('Paciente não encontrado');
      return;
    }

    await loadBankingInfo();

    const message = InvoiceService.formatInvoiceForWhatsApp(invoice, true);
    const encodedMessage = encodeURIComponent(message);
    const phone = patient.personalData.phone || '';

    const whatsappUrl = `https://wa.me/55${phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const getStatusIcon = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'overdue':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status: Invoice['status']) => {
    switch (status) {
      case 'paid': return 'Pago';
      case 'overdue': return 'Vencido';
      case 'cancelled': return 'Cancelado';
      case 'open': return 'Aberto';
      default: return 'Pendente';
    }
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handlePrintInvoice = async (invoice: Invoice & { patientName: string; patientId: string }) => {
    try {
      setIsPrinting(true);
      const patient = patients.find(p => p.id === invoice.patientId);
      await loadBankingInfo();

      await InvoicePDFService.printPDF({
        invoice,
        patientName: invoice.patientName,
        patientId: invoice.patientId,
        clientId: selectedClient?.clientId,
        selectedClient,
        patientDocument: patient?.personalData.cpf,
        patientAddress: undefined
      });

      toast.success('Documento preparado para impressão!');
    } catch (error: any) {
      toast.error(`Erro ao imprimir: ${error.message}`);
      console.error('Erro ao imprimir fatura:', error);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice & { patientName: string; patientId: string }) => {
    try {
      setIsPrinting(true);
      const patient = patients.find(p => p.id === invoice.patientId);
      await loadBankingInfo();

      await InvoicePDFService.downloadPDF({
        invoice,
        patientName: invoice.patientName,
        patientId: invoice.patientId,
        clientId: selectedClient?.clientId,
        selectedClient,
        patientDocument: patient?.personalData.cpf,
        patientAddress: undefined
      });

      toast.success('PDF baixado com sucesso!');
    } catch (error: any) {
      toast.error(`Erro ao baixar PDF: ${error.message}`);
      console.error('Erro ao baixar fatura:', error);
    } finally {
      setIsPrinting(false);
    }
  };

  const openEditModal = (invoice: Invoice & { patientName: string; patientId: string }) => {
    if (!invoiceActor || invoiceActor.userRole !== 'admin') {
      toast.error('Somente administradores podem editar faturas.');
      return;
    }
    if (invoice.status !== 'open' && invoice.status !== 'pending') {
      toast.error('Somente faturas com status open/pending podem ser alteradas.');
      return;
    }
    setSelectedInvoice(null);
    setEditingInvoice(invoice);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando faturas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Faturas</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalInvoices}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Valor Pago</p>
                <p className="text-2xl font-bold text-green-600">
                  R$ {summary.paidAmount.toFixed(2)}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Em Aberto</p>
                <p className="text-2xl font-bold text-yellow-600">
                  R$ {summary.openAmount.toFixed(2)}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Em Atraso</p>
                <p className="text-2xl font-bold text-red-600">
                  R$ {summary.overdueAmount.toFixed(2)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>
      )}

      {/* Modal de reversão de pagamento */}
      {showRevertModal && revertTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Reverter pagamento</h3>
              <button onClick={closeRevertModal} className="p-2 hover:bg-gray-100 rounded-lg">×</button>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Fatura</p>
                <div className="font-medium">{revertTarget.patientName} — R$ {revertTarget.total.toFixed(2)}</div>
                <div className="text-sm text-gray-500">Vencimento: {formatDate(revertTarget.dueDate)}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Justificativa (obrigatório)</label>
                <textarea
                  value={revertReason}
                  onChange={(e) => { setRevertReason(e.target.value); setRevertReasonError(null); }}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Explique por que o pagamento está sendo revertido (mínimo 10 caracteres)"
                />
                {revertReasonError && <div className="text-sm text-red-600 mt-1">{revertReasonError}</div>}
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={closeRevertModal} className="px-4 py-2 bg-gray-200 rounded">Cancelar</button>
                <button onClick={confirmRevert} className="px-4 py-2 bg-red-600 text-white rounded">Confirmar reversão</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <FinancialSearchSelect
                label="Paciente" required={false}
                options={patients.map(patient => ({ id: patient.id, displayName: patient.personalData.name }))}
                value={filter.patientId || ''}
                onChange={id => setFilter(prev => ({ ...prev, patientId: id || undefined }))}
                emptyOption={{ value: '', label: 'Todos os pacientes' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mês do Vencimento
              </label>
              <input
                type="month"
                value={filter.dueMonth || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, dueMonth: e.target.value || undefined }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filter.status || ''}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value as any || undefined }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos os status</option>
                <option value="open">Aberto</option>
                <option value="paid">Pago</option>
                <option value="overdue">Vencido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilter({})}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Faturas */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Faturas ({invoices.length})</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBankingConfig(true)}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
              >
                <Settings className="w-3 h-3" />
                Dados Bancários
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paciente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mês
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vencimento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map(invoice => {
                const invoiceKey = `${invoice.patientId}-${invoice.invoiceId}`;

                return (
                  <tr key={invoiceKey} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.patientName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.billingPeriodStart && invoice.billingPeriodEnd ? (
                        <span title="Período personalizado do pacote">
                          {new Date(invoice.billingPeriodStart).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} a {new Date(invoice.billingPeriodEnd).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      ) : (
                        formatMonthReference(invoice.month)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.type === 'package' ? 'Pacote' :
                       invoice.type === 'renewal' ? 'Renovação' : 'Individual'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      R$ {invoice.total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.dueDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        {getStatusLabel(invoice.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedInvoice(invoice)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrintInvoice(invoice)}
                          disabled={isPrinting}
                          className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                          title="Imprimir fatura"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(invoice)}
                          disabled={isPrinting}
                          className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                          title="Baixar PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { void sendInvoiceWhatsApp(invoice); }}
                          className="text-green-600 hover:text-green-900"
                          title="Enviar por WhatsApp"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        {(invoice.status === 'open' || invoice.status === 'pending') && (
                          <button
                            onClick={() => openEditModal(invoice)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Editar itens da fatura"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {invoice.status !== 'paid' ? (
                          <button
                            onClick={() => updateInvoiceStatus(invoice.patientId, invoice.invoiceId, 'paid')}
                            className="text-green-600 hover:text-green-900"
                            title="Marcar como pago"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => openRevertModal(invoice)}
                            className="text-red-600 hover:text-red-900"
                            title="Reverter pagamento"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {invoices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma fatura encontrada</p>
              <p className="text-sm">Ajuste os filtros ou gere novas faturas</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhes da Fatura */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Detalhes da Fatura</h2>
                <button 
                  onClick={() => setSelectedInvoice(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Informações básicas */}
                <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Paciente</label>
                  <div className="text-lg font-semibold">{selectedInvoice.patientName}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Período</label>
                  <div className="text-lg font-semibold">
                    {selectedInvoice.billingPeriodStart && selectedInvoice.billingPeriodEnd ? (
                      <>
                        {new Date(selectedInvoice.billingPeriodStart).toLocaleDateString('pt-BR')} a{' '}
                        {new Date(selectedInvoice.billingPeriodEnd).toLocaleDateString('pt-BR')}
                      </>
                    ) : (
                      selectedInvoice.month
                    )}
                  </div>
                </div>
              </div>

              {/* Vencimento */}
              <div>
                <label className="text-sm font-medium text-gray-600">Vencimento</label>
                <div className="text-lg font-semibold">{formatDate(selectedInvoice.dueDate)}</div>
              </div>

              {/* Detalhes de Agendamentos */}
              {selectedInvoice.appointmentDetails && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Histórico de Atendimentos</label>
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    {selectedInvoice.appointmentDetails.completed.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-green-900">
                            Sessões Realizadas ({selectedInvoice.appointmentDetails.completed.length})
                          </span>
                        </div>
                        <div className="ml-6 space-y-1">
                          {selectedInvoice.appointmentDetails.completed.map((apt, index) => (
                            <div key={index} className="text-sm text-gray-700">
                              {new Date(apt.date).toLocaleDateString('pt-BR')} - {apt.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedInvoice.appointmentDetails.noShow.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="font-medium text-red-900">
                            Faltas ({selectedInvoice.appointmentDetails.noShow.length})
                          </span>
                        </div>
                        <div className="ml-6 space-y-1">
                          {selectedInvoice.appointmentDetails.noShow.map((apt, index) => (
                            <div key={index} className="text-sm text-gray-700">
                              {new Date(apt.date).toLocaleDateString('pt-BR')} - {apt.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedInvoice.appointmentDetails.cancelled.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          <span className="font-medium text-orange-900">
                            Cancelamentos ({selectedInvoice.appointmentDetails.cancelled.length})
                          </span>
                        </div>
                        <div className="ml-6 space-y-1">
                          {selectedInvoice.appointmentDetails.cancelled.map((apt, index) => (
                            <div key={index} className="text-sm text-gray-700">
                              {new Date(apt.date).toLocaleDateString('pt-BR')} - {apt.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Itens */}
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 block">Detalhamento Financeiro</label>
                <div className="space-y-2">
                  {selectedInvoice.items.map((item, index) => (
                    <div key={index} className="flex justify-between p-3 bg-gray-50 rounded">
                      <div>
                        <div className="font-medium">{item.description}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(item.date).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                      <div className="font-medium">
                        R$ {item.amount.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total:</span>
                  <span>R$ {selectedInvoice.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Ações */}
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex gap-3">
                  <button
                    onClick={() => handlePrintInvoice(selectedInvoice)}
                    disabled={isPrinting}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Printer className="w-4 h-4" />
                    Imprimir
                  </button>
                  <button
                    onClick={() => handleDownloadInvoice(selectedInvoice)}
                    disabled={isPrinting}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    Baixar PDF
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { void sendInvoiceWhatsApp(selectedInvoice); }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Enviar WhatsApp
                  </button>
                  {(selectedInvoice.status === 'open' || selectedInvoice.status === 'pending') && (
                    <button
                      onClick={() => openEditModal(selectedInvoice)}
                      className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      Editar Itens
                    </button>
                  )}
                  {selectedInvoice.status !== 'paid' && (
                    <button
                      onClick={() => {
                        updateInvoiceStatus(selectedInvoice.patientId, selectedInvoice.invoiceId, 'paid');
                        setSelectedInvoice(null);
                      }}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Marcar como Pago
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceEditModal
        isOpen={Boolean(editingInvoice)}
        invoice={editingInvoice}
        actor={invoiceActor}
        onClose={() => setEditingInvoice(null)}
        onUpdated={loadInvoices}
      />

      <BankingConfigModal
        isOpen={showBankingConfig}
        clientId={selectedClient?.clientId}
        clientName={selectedClient?.clientName}
        initialValue={bankingInfo}
        onClose={() => setShowBankingConfig(false)}
        onSaved={(savedBankingInfo) => {
          setBankingInfo(savedBankingInfo);
          InvoiceService.setBankingInfo(savedBankingInfo, selectedClient?.clientId);
        }}
      />
    </div>
  );
}
